// ============================================================================
// Validación de identificación tributaria (Colombia) — jun-2026.
//
// Estrategia (validada con el usuario):
//   1) NIT  → se calcula el DÍGITO DE VERIFICACIÓN oficial (DIAN) y se compara
//      con el que digitó el usuario. Es 100% local y atrapa la mayoría de
//      errores de digitación.
//   2) Cédula → validación de formato (solo dígitos, largo razonable).
//   3) La existencia "real" del tercero la confirma la DIAN al emitir la
//      factura (si el receptor es inválido, la rechaza) — eso se maneja con la
//      respuesta de dian-billing, no aquí.
//
// Códigos de tipo de documento (DIAN): '31' = NIT, '13' = Cédula de ciudadanía,
// '22' = Cédula de extranjería, '41' = Pasaporte, '11'/'12' = registro civil/TI.
// Consumidor final = identificación '222222222'.
// ============================================================================

export interface ValidacionResultado {
  ok: boolean;
  mensaje?: string;
  /** Para NIT: dígito de verificación calculado (útil para mostrar/autocompletar). */
  dv?: number;
  /** Número normalizado (sin puntos/espacios). */
  normalizado?: string;
}

const PESOS_NIT = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];

/**
 * Dígito de verificación de un NIT colombiano (base SIN el DV).
 * Algoritmo oficial DIAN: Σ(dígito_i × peso_i) mod 11, leyendo de derecha a
 * izquierda; si el resto es 0 ó 1 el DV es el resto, si no es 11 − resto.
 */
export function digitoVerificacionNit(base: string): number {
  const digits = String(base).replace(/\D/g, '').split('').reverse();
  let suma = 0;
  digits.forEach((d, i) => { suma += Number(d) * (PESOS_NIT[i] ?? 0); });
  const resto = suma % 11;
  return resto > 1 ? 11 - resto : resto;
}

/**
 * Valida un NIT. Acepta "900.123.456-7", "900123456-7" o "900123456" (sin DV).
 * Si trae DV, verifica que sea el correcto. Si no, devuelve ok con el DV
 * calculado para que la UI lo muestre/autocomplete.
 */
export function validarNit(input: string): ValidacionResultado {
  const limpio = String(input).replace(/[.\s]/g, '');
  const m = limpio.match(/^(\d{5,15})(?:-(\d))?$/);
  if (!m) {
    return { ok: false, mensaje: 'NIT inválido. Usá solo números y, opcionalmente, "-" antes del dígito de verificación.' };
  }
  const base = m[1];
  const dv = digitoVerificacionNit(base);
  if (m[2] != null && Number(m[2]) !== dv) {
    return { ok: false, mensaje: `El dígito de verificación no coincide (debería ser ${dv}).`, dv, normalizado: base };
  }
  return { ok: true, dv, normalizado: base };
}

/** Valida el formato de una cédula (ciudadanía/extranjería). */
export function validarCedula(input: string): ValidacionResultado {
  const limpio = String(input).replace(/[.\s]/g, '');
  if (!/^\d{5,10}$/.test(limpio)) {
    return { ok: false, mensaje: 'La cédula debe tener entre 5 y 10 dígitos.' };
  }
  return { ok: true, normalizado: limpio };
}

/**
 * Valida una identificación según su tipo de documento DIAN. Pensado para
 * usarse cuando el cliente pide factura electrónica.
 */
export function validarIdentificacion(tipoDocumento: string, numero: string): ValidacionResultado {
  const num = String(numero ?? '').trim();
  if (!num) return { ok: false, mensaje: 'Ingresá la identificación del cliente.' };

  // Consumidor final — válido sin verificación.
  if (num.replace(/[.\s-]/g, '') === '222222222') return { ok: true, normalizado: '222222222' };

  switch (String(tipoDocumento)) {
    case '31': // NIT
      return validarNit(num);
    case '13': // Cédula de ciudadanía
    case '22': // Cédula de extranjería
      return validarCedula(num);
    case '41': // Pasaporte — alfanumérico, solo exigimos no-vacío y largo razonable
      return /^[A-Za-z0-9]{5,20}$/.test(num.replace(/\s/g, ''))
        ? { ok: true }
        : { ok: false, mensaje: 'Pasaporte inválido (5–20 caracteres alfanuméricos).' };
    default:
      // Otros tipos: exigimos al menos un número.
      return /^\d{4,}$/.test(num.replace(/[.\s-]/g, ''))
        ? { ok: true }
        : { ok: false, mensaje: 'Identificación inválida.' };
  }
}
